import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import * as intlTelInput from 'intl-tel-input';

import { PhoneService } from '@app/services';

@Component({
  selector: 'app-phone-number',
  templateUrl: './phone-number.component.html',
  styleUrls: ['./phone-number.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneNumberComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneNumberComponent),
      multi: true
    }
  ],
  standalone: false
})
export class PhoneNumberComponent implements ControlValueAccessor, Validator, AfterViewInit, OnChanges, OnDestroy {
  @Input('value') phoneNumberValue: string;
  @Input() formControlName: string;
  @Input() name = '';
  @Input() inputClass = '';
  @Input() disabled = false;
  @Input() autocomplete = 'off';
  @Input() required = false;
  @Output() validationError = new EventEmitter<boolean>();
  @ViewChild('phoneInputElement', { static: false }) private phoneInputElement: ElementRef<HTMLInputElement>;
  partialValue = '';
  private onChangeCallback: (_: string) => void = () => {};
  private onTouchedCallback: () => void = () => {};
  private onValidatorChangeCallback: () => void = () => {};
  private phoneInput: any;
  private isPhoneInputReady = false;
  private isHandlingInput = false;
  private readonly boundCountryChangeHandler = this.handleCountryChange.bind(this);

  ngOnChanges(changes: SimpleChanges) {
    if (changes.phoneNumberValue && !changes.phoneNumberValue.firstChange) {
      this.applyExternalValue(changes.phoneNumberValue.currentValue || '');
    }
  }

  ngAfterViewInit() {
    this.phoneInput = intlTelInput(this.phoneInputElement.nativeElement, {
      initialCountry: 'br',
      preferredCountries: ['br', 'pt', 'us'],
      autoHideDialCode: false,
      nationalMode: true,
      placeholderNumberType: 'MOBILE',
      autoPlaceholder: 'aggressive',
      formatOnDisplay: false,
      utilsScript: PhoneService.intlTelUtilsScript()
    });

    this.phoneInputElement.nativeElement.addEventListener('countrychange', this.boundCountryChangeHandler);

    const onPhoneInputReady = () => this.handlePhoneInputReady();

    if (this.phoneInput.promise) {
      this.phoneInput.promise.then(onPhoneInputReady, onPhoneInputReady);
      return;
    }

    onPhoneInputReady();
  }

  writeValue(phoneNumberValue: string) {
    this.applyExternalValue(phoneNumberValue || '');

    if (phoneNumberValue) {
      this.onTouchedCallback();
    }
  }

  ngOnDestroy() {
    if (this.phoneInputElement?.nativeElement) {
      this.phoneInputElement.nativeElement.removeEventListener('countrychange', this.boundCountryChangeHandler);
    }

    this.phoneInput?.destroy?.();
  }

  registerOnChange(fn: (_: any) => {}) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => {}) {
    this.onTouchedCallback = fn;
  }

  registerOnValidatorChange(fn: () => void) {
    this.onValidatorChangeCallback = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  onInput(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    this.processInputValue(inputElement.value, inputElement.selectionStart ?? inputElement.value.length);
  }

  onBlur() {
    this.onTouchedCallback();
  }

  private handleCountryChange() {
    if (!this.isHandlingInput) {
      this.reformatInputValue();
    }
  }

  private handlePhoneInputReady() {
    this.isPhoneInputReady = true;
    this.syncDisplayFromValue(this.phoneNumberValue || '');
  }

  private applyExternalValue(phoneNumberValue: string) {
    this.phoneNumberValue = phoneNumberValue;

    if (!this.isPhoneInputReady) {
      const sanitizedValue = PhoneService.sanitizePhoneInput(phoneNumberValue);
      this.partialValue = sanitizedValue ? PhoneService.formatPhoneInput(sanitizedValue, this.getSelectedCountryCode()) : '';
      return;
    }

    this.syncDisplayFromValue(phoneNumberValue);
  }

  private syncDisplayFromValue(phoneNumberValue: string) {
    if (!this.phoneInput || !this.phoneInputElement) {
      return;
    }

    if (!phoneNumberValue) {
      this.writeDisplayValue('');
      this.emitValidationState();
      return;
    }

    this.isHandlingInput = true;

    try {
      this.phoneInput.setNumber(phoneNumberValue);
    } finally {
      this.isHandlingInput = false;
    }

    const sanitizedValue = PhoneService.sanitizePhoneInput(this.phoneInputElement.nativeElement.value || phoneNumberValue);
    const formattedValue = PhoneService.formatPhoneInput(sanitizedValue, this.getSelectedCountryCode());
    this.writeDisplayValue(formattedValue);
    this.emitValidationState();
  }

  private processInputValue(inputValue: string, selectionStart: number) {
    if (!this.phoneInputElement) {
      return;
    }

    this.isHandlingInput = true;

    try {
      const sanitizedValue = PhoneService.sanitizePhoneInput(inputValue);
      this.syncCountryFromInput(sanitizedValue);

      const formattedValue = PhoneService.formatPhoneInput(sanitizedValue, this.getSelectedCountryCode());
      const nextCursorPosition = this.getCursorPosition(inputValue, formattedValue, selectionStart);

      this.writeDisplayValue(formattedValue, nextCursorPosition);
      this.emitValue(sanitizedValue);
    } finally {
      this.isHandlingInput = false;
    }
  }

  private reformatInputValue() {
    const inputElement = this.phoneInputElement?.nativeElement;

    if (!inputElement) {
      return;
    }

    this.processInputValue(inputElement.value, inputElement.selectionStart ?? inputElement.value.length);
  }

  private syncCountryFromInput(phoneNumber: string) {
    if (!phoneNumber.startsWith('+')) {
      return;
    }

    const detectedCountry = PhoneService.detectCountryFromPhoneInput(phoneNumber);

    if (detectedCountry && detectedCountry !== this.getSelectedCountryCode()) {
      this.phoneInput.setCountry(detectedCountry);
    }
  }

  private getSelectedCountryCode() {
    return this.phoneInput?.getSelectedCountryData()?.iso2 || null;
  }

  private writeDisplayValue(phoneNumberValue: string, cursorPosition?: number) {
    const inputElement = this.phoneInputElement.nativeElement;

    this.partialValue = phoneNumberValue;
    inputElement.value = phoneNumberValue;

    if (document.activeElement === inputElement && cursorPosition !== undefined) {
      inputElement.setSelectionRange(cursorPosition, cursorPosition);
    }
  }

  private emitValue(partialValue: string) {
    const numberFromLib = this.isPhoneInputReady && this.phoneInput ? this.phoneInput.getNumber() : partialValue || '';
    if (numberFromLib && numberFromLib.startsWith('+')) {
      // getNumber() só retorna E.164 quando consegue validar o número
      this.phoneNumberValue = numberFromLib.replace(/\s/g, '');
    } else {
      // fallback manual, garante +DDI mesmo com número parcial/inválido
      const dialCode = this.phoneInput?.getSelectedCountryData()?.dialCode;
      this.phoneNumberValue = partialValue ? `+${dialCode}${partialValue}` : '';
    }

    setTimeout(() => this.onChangeCallback(this.phoneNumberValue));
    this.emitValidationState();
  }

  private emitValidationState() {
    this.validationError.emit(!(!this.isPhoneInputReady || !this.phoneInput || this.phoneInput.isValidNumber()));
    this.onValidatorChangeCallback();
  }

  private getCursorPosition(previousValue: string, nextValue: string, selectionStart: number) {
    const phoneCharsBeforeCursor = PhoneService.sanitizePhoneInput(previousValue.slice(0, selectionStart)).length;

    if (!phoneCharsBeforeCursor) {
      return 0;
    }

    let currentPhoneChars = 0;

    for (let index = 0; index < nextValue.length; index++) {
      if (/[+\d]/.test(nextValue[index])) {
        currentPhoneChars += 1;

        if (currentPhoneChars === phoneCharsBeforeCursor) {
          return index + 1;
        }
      }
    }

    return nextValue.length;
  }

  validate(control: UntypedFormControl): ValidationErrors | null {
    return !this.isPhoneInputReady || !this.phoneInput || !this.phoneInput.getNumber() || this.phoneInput.isValidNumber() ? null : { phoneError: { valid: false } };
  }
}
